/**
 * AdminGeoSeoPipeline — Monitor & control the Geo SEO content generation pipeline.
 * Cities × 7 services = unique 750-word SEO pages.
 * Runs in 15-page chunks every 5 minutes via the unified scheduler.
 */
import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import {
  Play, Pause, RefreshCw, MapPin, FileText, CheckCircle2,
  AlertTriangle, Rocket, Globe,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const SERVICE_LABELS = {
  'local-seo': 'Local SEO',
  'social-media': 'Social Media',
  'reputation-management': 'Reputation Management',
  'google-ads': 'Google Ads & PPC',
  'web-design': 'Web Design',
  'content-marketing': 'Content Marketing',
  'google-business-profile': 'Google Business Profile',
};

export default function AdminGeoSeoPipeline({ getAuthHeaders }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionBusy, setActionBusy] = useState(false);
  const [error, setError] = useState(null);
  const [cityCount, setCityCount] = useState(null);
  const [seedResult, setSeedResult] = useState(null);

  const headers = useCallback(() => {
    if (getAuthHeaders) return getAuthHeaders();
    const token = localStorage.getItem('admin_token');
    return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  }, [getAuthHeaders]);

  const fetchStatus = useCallback(async () => {
    try {
      const h = headers();
      const [statusRes, countRes] = await Promise.all([
        fetch(`${API}/admin/geo-seo/status`, { headers: h }).then(r => r.json()),
        fetch(`${API}/admin/geo-seo/city-count`, { headers: h }).then(r => r.json()).catch(() => null),
      ]);
      setStatus(statusRes);
      if (countRes) setCityCount(countRes);
      setError(null);
    } catch (e) {
      const msg = e.message;
      if (msg && !/canceled|aborted/i.test(msg)) setError(msg);
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => {
    fetchStatus();
    const id = setInterval(fetchStatus, 15000);
    return () => clearInterval(id);
  }, [fetchStatus]);

  const doAction = async (path, confirmMsg) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    setActionBusy(true);
    setSeedResult(null);
    try {
      const res = await fetch(`${API}${path}`, { method: 'POST', headers: headers() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Action failed');
      if (path.includes('seed')) setSeedResult(data);
      await fetchStatus();
    } catch (e) {
      setError(e.message);
    } finally {
      setActionBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 p-6 text-slate-400" data-testid="geo-seo-loading">
        <RefreshCw className="h-4 w-4 animate-spin" /> Loading pipeline status...
      </div>
    );
  }

  const total = status?.total_possible || 0;
  const generated = status?.total_pages_generated || 0;
  const eligible = status?.eligible_cities || cityCount?.cities || 0;
  const remaining = Math.max(0, total - generated);
  const percent = total ? Math.round((generated / total) * 1000) / 10 : 0;
  const autoRun = !!status?.auto_run;
  const pipeStatus = status?.pipeline_status || 'idle';

  return (
    <div className="space-y-6" data-testid="geo-seo-pipeline-panel">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Globe className="h-6 w-6 text-cyan-600" />
            Geo SEO Content Pipeline
          </h2>
          <p className="text-slate-500 mt-1 text-sm">
            Auto-generate unique 750-word SEO pages for every U.S. city x service
            ({total.toLocaleString()} total pages). Runs 15 pages every 5 minutes.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {cityCount && cityCount.cities === 0 && (
            <Button
              onClick={() => doAction('/admin/geo-seo/seed-cities', 'Seed US cities into the database?')}
              disabled={actionBusy}
              className="bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-500 hover:to-orange-500"
              data-testid="geo-seo-seed-btn"
            >
              <MapPin className="h-4 w-4 mr-2" />
              Seed Cities
            </Button>
          )}
          {!autoRun ? (
            <Button
              onClick={() => doAction('/admin/geo-seo/start-full-run')}
              disabled={actionBusy}
              className="bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500"
              data-testid="geo-seo-start-full-btn"
            >
              <Rocket className="h-4 w-4 mr-2" />
              Start Full Auto-Run
            </Button>
          ) : (
            <Button
              onClick={() => doAction('/admin/geo-seo/pause-run')}
              disabled={actionBusy}
              variant="outline"
              className="border-yellow-500 text-yellow-600 hover:bg-yellow-50"
              data-testid="geo-seo-pause-btn"
            >
              <Pause className="h-4 w-4 mr-2" />
              Pause Auto-Run
            </Button>
          )}
          <Button
            onClick={() => doAction('/admin/geo-seo/run-chunk-now?chunk_size=15')}
            disabled={actionBusy}
            variant="outline"
            data-testid="geo-seo-run-now-btn"
          >
            <Play className="h-4 w-4 mr-2" />
            Run Chunk Now
          </Button>
          <Button
            onClick={() => doAction('/admin/geo-seo/generate-es-batch?limit=20', 'Generate Spanish (ES) twin pages for up to 20 English pages? This uses LLM credits.')}
            disabled={actionBusy}
            variant="outline"
            className="border-fuchsia-500 text-fuchsia-600 hover:bg-fuchsia-50"
            data-testid="geo-seo-generate-es-btn"
          >
            <Globe className="h-4 w-4 mr-2" />
            Generate Spanish Pages
          </Button>
          <Button
            onClick={() => doAction(`/admin/geo-seo/toggle-es-auto?enabled=${status?.auto_es ? 'false' : 'true'}&daily_cap=${status?.es_daily_cap || 100}`)}
            disabled={actionBusy}
            variant="outline"
            className={status?.auto_es ? 'border-fuchsia-600 bg-fuchsia-50 text-fuchsia-700' : 'border-slate-300 text-slate-600'}
            data-testid="geo-seo-toggle-es-auto-btn"
          >
            <Globe className="h-4 w-4 mr-2" />
            {status?.auto_es ? 'Auto-ES: ON' : 'Auto-ES: OFF'}
          </Button>
          <Button onClick={fetchStatus} disabled={actionBusy} variant="ghost" size="icon" data-testid="geo-seo-refresh-btn">
            <RefreshCw className={`h-4 w-4 ${actionBusy ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 rounded bg-red-50 border border-red-200 text-red-800 text-sm font-medium" data-testid="geo-seo-error-banner">
          <AlertTriangle className="h-4 w-4 flex-shrink-0" /> <span>{error}</span>
          <button onClick={() => setError(null)} className="ml-auto text-red-600 hover:underline text-xs" data-testid="geo-seo-error-dismiss">Dismiss</button>
        </div>
      )}

      {seedResult && (
        <div className="flex items-center gap-2 p-3 rounded bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm font-medium">
          <CheckCircle2 className="h-4 w-4" /> {seedResult.message}
        </div>
      )}

      {cityCount && typeof cityCount.es_pages === 'number' && (
        <div className="flex items-center gap-2 p-3 rounded bg-fuchsia-50 border border-fuchsia-200 text-fuchsia-800 text-sm" data-testid="geo-seo-es-coverage">
          <Globe className="h-4 w-4 flex-shrink-0" />
          <span><strong>{cityCount.es_pages.toLocaleString()}</strong> Spanish (ES) twin pages generated
          {' '}({cityCount.es_coverage_pct}% of {cityCount.generated_pages.toLocaleString()} English pages).
          {status?.auto_es
            ? ` Auto-ES is ON — generating up to ${status.es_daily_cap}/day (${status.es_today_count || 0} today).`
            : ' Turn on "Auto-ES" to grow bilingual coverage automatically, or click "Generate Spanish Pages".'}
          </span>
        </div>
      )}

      {cityCount && cityCount.cities === 0 && !seedResult && (
        <div className="flex items-center gap-2 p-3 rounded bg-amber-50 border border-amber-200 text-amber-800 text-sm font-medium">
          <AlertTriangle className="h-4 w-4" /> No cities in database. Click "Seed Cities" above to initialize, then start the auto-run.
        </div>
      )}

      {/* Status cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="text-xs uppercase tracking-wide text-slate-400 mb-1">Status</div>
            <div className="flex items-center gap-2">
              {autoRun ? (
                <Badge className="bg-green-600 hover:bg-green-600 text-white">LIVE - AUTO-RUN</Badge>
              ) : percent >= 100 ? (
                <Badge className="bg-blue-600 hover:bg-blue-600 text-white">COMPLETE</Badge>
              ) : (
                <Badge variant="outline">{pipeStatus.toUpperCase()}</Badge>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="text-xs uppercase tracking-wide text-slate-400 mb-1">Pages Generated</div>
            <div className="text-2xl font-bold text-slate-800" data-testid="geo-seo-generated">{generated.toLocaleString()}</div>
            <div className="text-xs text-slate-500 mt-1">of {total.toLocaleString()}</div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="text-xs uppercase tracking-wide text-slate-400 mb-1">Cities Covered</div>
            <div className="text-2xl font-bold text-slate-800 flex items-center gap-1">
              <MapPin className="h-5 w-5 text-cyan-500" />
              {(status?.unique_cities_covered || 0).toLocaleString()}
            </div>
            <div className="text-xs text-slate-500 mt-1">of {eligible.toLocaleString()}</div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="text-xs uppercase tracking-wide text-slate-400 mb-1">Remaining</div>
            <div className="text-2xl font-bold text-slate-800">{remaining.toLocaleString()}</div>
            <div className="text-xs text-slate-500 mt-1">~{Math.ceil(remaining / 180)} hours @ 180/hr</div>
          </CardContent>
        </Card>
      </div>

      {/* Progress bar */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center justify-between">
            <span>Overall Progress</span>
            <span className="text-cyan-600 font-mono text-sm">{percent}%</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Progress value={percent} className="h-3" data-testid="geo-seo-progress" />
          <div className="flex justify-between text-xs text-slate-500 mt-2">
            <span>{generated.toLocaleString()} done</span>
            <span>{remaining.toLocaleString()} to go</span>
          </div>
          {status?.last_chunk_at && (
            <div className="text-xs text-slate-400 mt-3">
              Last chunk: {new Date(status.last_chunk_at).toLocaleString()} —
              processed <span className="font-semibold text-slate-700">{status.last_chunk_processed || 0}</span> pages
            </div>
          )}
        </CardContent>
      </Card>

      {/* Per-service breakdown */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <FileText className="h-5 w-5 text-cyan-500" />
            Pages per Service
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {(status?.services_available || []).map((sid) => {
              const count = status?.pages_per_service?.[sid] || 0;
              const pct = eligible > 0 ? Math.round((count / eligible) * 100) : 0;
              return (
                <div key={sid} className="p-3 rounded border border-slate-200 bg-slate-50">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-sm font-medium text-slate-800">
                      {SERVICE_LABELS[sid] || sid.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                    </span>
                    {count >= eligible && eligible > 0 ? (
                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                    ) : (
                      <span className="text-xs text-slate-400">{pct}%</span>
                    )}
                  </div>
                  <Progress value={pct} className="h-1.5" />
                  <div className="text-xs text-slate-500 mt-1.5">
                    {count.toLocaleString()} / {eligible.toLocaleString()}
                  </div>
                </div>
              );
            })}
          </div>
          {status?.errors > 0 && (
            <div className="mt-4 flex items-center gap-2 text-sm text-amber-600">
              <AlertTriangle className="h-4 w-4" />
              {status.errors} generation errors (skipped &amp; will retry on next run)
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
